"use client";
import { useTranslations } from "next-intl";
import KibbeGeneratedReport, { GeneratedKibbeReport } from "@/components/kibbe/KibbeGeneratedReport";
import KibbeWardrobePlan from "@/components/kibbe/KibbeWardrobePlan";
import type { KibbeWardrobePlan as WardrobePlan } from "@/lib/kibbeReport";

import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Box,
  SimpleGrid,
  Button,
  Progress,
  Loader,
  Accordion,
} from "@mantine/core";
import { useRouter } from "next/navigation";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import firebase_app from "@/firebase/config";
import ColorAnalysisResults from "./ColorAnalysisResults";
import KibbeMoodStrip from "./KibbeMoodStrip";

interface KibbeResult {
  wardrobePlan?: WardrobePlan;
  bodyType: string;
  confidence_level: number;
  gender: string;
  explanation: string;
  visualMetrics: {
    yinYangBalance: number;
    proportions: {
      shoulders: number;
      waist: number;
      hips: number;
    };
    facialFeatures: {
      jawline: number;
      nose: number;
      cheekbones: number;
      eyes: number;
      lips: number;
    };
  };
  boneStructure: {
    shoulders: string;
    armsLegs: string;
    handsFeet: string;
  };
  bodyFlesh: {
    bustChest: string;
    waist: string;
    hips: string;
    fleshOnArmsLegs: string;
  };
  facialFeatures: {
    jawline: string;
    nose: string;
    cheekbones: string;
    eyes: string;
    lips: string;
  };
  personalizedInsights?: {
    standoutFeature: string;
    styleEssence: string;
    dressForStrength: string;
    minimizeAdvice: string;
  };
  whyNotOtherTypes?: Array<{
    type: string;
    reason: string;
  }>;
  fitRules?: {
    shoulderFit: string;
    hemLength: string;
    waistPlacement: string;
    sleeveLength: string;
    proportionRule: string;
  };
  commonMistakes?: Array<{
    mistake: string;
    why: string;
    instead: string;
  }>;
  shoppingTips?: {
    brandsToTry: string[];
    searchTerms: string[];
    budgetHacks: string[];
    redFlags: string[];
  };
  styleRecommendations: {
    do: string[];
    dont: string[];
  };
}

interface GeneratedOutfit {
  name: string;
  type: string;
  description: string;
  imagePath: string;
}

interface AnalysisViewProps {
  sample?: boolean;
  analysisId: string;
  analysis: any;
  loading: boolean;
  error: string | null;
  colorAnalysisId: string | null;
  colorAnalysisStatus: "pending" | "completed" | "failed" | null;
}

// Celebrity data for each type
const kibbeTypeCelebrities: Record<string, string[]> = {
  Dramatic: ["Tilda Swinton", "Cate Blanchett", "Taylor Swift", "Jamie Lee Curtis"],
  "Soft Dramatic": ["Sofia Vergara", "Adele", "Christina Hendricks", "Tyra Banks"],
  "Flamboyant Natural": ["Jennifer Lawrence", "Julia Roberts", "Cindy Crawford", "Princess Diana"],
  Natural: ["Jennifer Aniston", "Cameron Diaz", "Diane Keaton"],
  "Soft Natural": ["Scarlett Johansson", "Drew Barrymore", "Gigi Hadid", "Shakira"],
  "Dramatic Classic": ["Jackie Kennedy", "Olivia Palermo", "Kate Middleton"],
  Classic: ["Grace Kelly", "Meryl Streep", "Diane Sawyer"],
  "Soft Classic": ["Naomi Watts", "Dakota Johnson", "Marion Cotillard"],
  "Flamboyant Gamine": ["Audrey Hepburn", "Emma Watson", "Zendaya", "Liza Minnelli"],
  Gamine: ["Winona Ryder", "Mia Farrow", "Twiggy"],
  "Soft Gamine": ["Reese Witherspoon", "Halle Berry", "Sarah Jessica Parker", "Jenna Coleman"],
  "Theatrical Romantic": ["Salma Hayek", "Vivien Leigh", "Mila Kunis"],
  Romantic: ["Marilyn Monroe", "Elizabeth Taylor", "Beyoncé", "Kate Winslet"],
};

// Silhouette images per type
const kibbeSilhouettes: Record<string, string> = {
  Dramatic: "/kibbe/silhouettes/dramatic.png",
  "Soft Dramatic": "/kibbe/silhouettes/soft-dramatic.png",
  "Flamboyant Natural": "/kibbe/silhouettes/flamboyant-natural.png",
  Natural: "/kibbe/silhouettes/natural.png",
  "Soft Natural": "/kibbe/silhouettes/soft-natural.png",
  "Dramatic Classic": "/kibbe/silhouettes/dramatic-classic.png",
  Classic: "/kibbe/silhouettes/classic.png",
  "Soft Classic": "/kibbe/silhouettes/soft-classic.png",
  "Flamboyant Gamine": "/kibbe/silhouettes/flamboyant-gamine.png",
  Gamine: "/kibbe/silhouettes/gamine.png",
  "Soft Gamine": "/kibbe/silhouettes/soft-gamine.png",
  "Theatrical Romantic": "/kibbe/silhouettes/theatrical-romantic.png",
  Romantic: "/kibbe/silhouettes/romantic.png",
};

// Map types to their guide family
const typeToGuideFamily: Record<string, string> = {
  Dramatic: "dramatic",
  "Soft Dramatic": "dramatic",
  "Dramatic Classic": "dramatic",
  "Flamboyant Natural": "natural",
  Natural: "natural",
  "Soft Natural": "natural",
  Classic: "classic",
  "Soft Classic": "classic",
  "Flamboyant Gamine": "gamine",
  Gamine: "gamine",
  "Soft Gamine": "gamine",
  Romantic: "romantic",
  "Theatrical Romantic": "romantic",
};

// Type identity statements
const typeIdentityStatements: Record<string, { headline: string; strength: string }> = {
  Dramatic: { headline: "the striking angular type", strength: "bold, unbroken vertical lines and sharp geometry" },
  "Soft Dramatic": { headline: "the bold yet sensual type", strength: "dramatic silhouettes that honor your curves" },
  "Flamboyant Natural": { headline: "the free-spirited statuesque type", strength: "relaxed, unconstructed pieces with bold presence" },
  Natural: { headline: "the effortlessly relaxed type", strength: "casual elegance with natural, unfussy lines" },
  "Soft Natural": { headline: "the approachable balanced type", strength: "relaxed structure with gentle softness" },
  "Dramatic Classic": { headline: "the polished angular type", strength: "refined tailoring with a sharp edge" },
  Classic: { headline: "the timelessly balanced type", strength: "symmetrical, moderate silhouettes" },
  "Soft Classic": { headline: "the gracefully balanced type", strength: "soft tailoring with feminine details" },
  "Flamboyant Gamine": { headline: "the edgy petite type", strength: "crisp, bold contrasts in a compact frame" },
  Gamine: { headline: "the playfully contrasted type", strength: "mixing opposites with youthful energy" },
  "Soft Gamine": { headline: "the charming petite type", strength: "playful details with soft femininity" },
  "Theatrical Romantic": { headline: "the dramatic feminine type", strength: "ornate, curve-hugging glamour" },
  Romantic: { headline: "the softly feminine type", strength: "flowing, figure-hugging silhouettes" },
};

// Common frustrations for each type
const typeFrustrations: Record<string, string[]> = {
  Dramatic: ["'Relaxed fit' looks sloppy", "Oversized trends overwhelm", "Soft fabrics feel shapeless", "Petite cuts look awkward"],
  "Soft Dramatic": ["Structured blazers feel stiff", "Minimalist styles look plain", "Hiding curves adds bulk", "Delicate jewelry gets lost"],
  "Flamboyant Natural": ["Tailored styles feel restrictive", "Fussy details look wrong", "Structured shoulders feel stiff", "Short hems throw proportions"],
  Natural: ["Fitted clothes feel uncomfortable", "Elaborate styling looks overdone", "Stiff fabrics feel wrong", "Polished looks feel fake"],
  "Soft Natural": ["Oversized trends overwhelm", "Ultra-tailored feels harsh", "Stiff fabrics feel wrong", "Shapeless silhouettes erase waist"],
  "Soft Classic": ["Sharp styles feel harsh", "Oversized clothes swallow you", "Bold prints overwhelm", "Structured shoulders feel stiff"],
  Classic: ["Bold statements feel 'too much'", "Asymmetrical cuts look unbalanced", "Trendy extremes don't work", "Casual styles look underdone"],
  "Dramatic Classic": ["Casual styles look sloppy", "Soft fabrics feel shapeless", "Rounded shapes don't suit", "Bohemian styles look off"],
  "Flamboyant Gamine": ["Romantic styles look costume-like", "Long lines drown you", "Drapey fabrics feel shapeless", "Sophisticated looks age you"],
  Gamine: ["Elongating styles don't work", "Romantic pieces feel off", "Monochrome falls flat", "Long hems throw proportions"],
  "Soft Gamine": ["Minimal styles look incomplete", "Oversized clothes drown you", "Angular pieces feel harsh", "Flowing silhouettes overwhelm"],
  "Theatrical Romantic": ["Minimalist looks underdressed", "Boxy shapes hide figure", "Matte fabrics feel frumpy", "Classic tailoring looks boring"],
  Romantic: ["Angular styles feel harsh", "Structured pieces don't drape", "Minimalist outfits feel incomplete", "Stiff fabrics fight curves"],
};

// Trait comparison data
const traitComparisons: Record<string, Record<string, string>> = {
  Dramatic: { shoulders: "Sharp, angular", flesh: "Minimal, taut", vertical: "Long", overall: "Sharp Yang" },
  "Soft Dramatic": { shoulders: "Sharp with width", flesh: "Soft, curved", vertical: "Long", overall: "Yang + Yin curves" },
  "Flamboyant Natural": { shoulders: "Broad, blunt", flesh: "Moderate", vertical: "Long", overall: "Blunt Yang" },
  Natural: { shoulders: "Moderate width", flesh: "Moderate", vertical: "Moderate", overall: "Relaxed Yang" },
  "Soft Natural": { shoulders: "Slight width", flesh: "Soft, curved", vertical: "Moderate", overall: "Soft Yang" },
  "Dramatic Classic": { shoulders: "Slightly sharp", flesh: "Moderate", vertical: "Moderate-long", overall: "Balanced + Yang" },
  Classic: { shoulders: "Balanced", flesh: "Moderate", vertical: "Moderate", overall: "Perfect balance" },
  "Soft Classic": { shoulders: "Slightly rounded", flesh: "Soft", vertical: "Moderate", overall: "Balanced + Yin" },
  "Flamboyant Gamine": { shoulders: "Sharp, angular", flesh: "Compact", vertical: "Short", overall: "Petite Yang" },
  Gamine: { shoulders: "Mixed", flesh: "Compact", vertical: "Short", overall: "Yin/Yang mix" },
  "Soft Gamine": { shoulders: "Soft, small", flesh: "Soft, rounded", vertical: "Short", overall: "Petite Yin" },
  "Theatrical Romantic": { shoulders: "Narrow, delicate", flesh: "Lush, curved", vertical: "Short-moderate", overall: "Dramatic Yin" },
  Romantic: { shoulders: "Rounded, soft", flesh: "Lush, soft", vertical: "Short-moderate", overall: "Pure Yin" },
};

export default function AnalysisView({
  analysisId,
  analysis,
  loading,
  error,
  colorAnalysisId,
  colorAnalysisStatus,
  sample = false,
}: AnalysisViewProps) {
  const t = useTranslations("kibbeFlow.report");
  const router = useRouter();
  const result = analysis?.result as KibbeResult;

  const [colorAnalysis, setColorAnalysis] = useState<any>(null);
  const [colorAnalysisLoading, setColorAnalysisLoading] = useState(false);
  const [outfitImageUrls, setOutfitImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (colorAnalysisId) {
      setColorAnalysisLoading(true);
      const firestore = getFirestore(firebase_app);
      const analysisRef = doc(firestore, "generatedColorAnalyses", colorAnalysisId);
      const unsubscribe = onSnapshot(
        analysisRef,
        (docSnap) => {
          if (docSnap.exists()) {
            setColorAnalysis(docSnap.data());
          }
          setColorAnalysisLoading(false);
        },
        () => {
          setColorAnalysisLoading(false);
        }
      );
      return () => unsubscribe();
    }
  }, [colorAnalysisId]);

  useEffect(() => {
    const loadOutfitUrls = async () => {
      if (!analysis?.generatedOutfits || analysis.generatedOutfits.length === 0) return;
      const { getStorage, ref, getDownloadURL } = await import("firebase/storage");
      const storage = getStorage(firebase_app);
      const urls: Record<string, string> = {};
      for (const outfit of analysis.generatedOutfits) {
        if (sample && (outfit.imagePath.startsWith("/kibbe-outfits/") || outfit.imagePath.startsWith("/kibbe-samples/"))) { urls[outfit.name] = outfit.imagePath; continue; }
        try {
          const imageRef = ref(storage, outfit.imagePath);
          const url = await getDownloadURL(imageRef);
          urls[outfit.name] = url;
        } catch (err) {
          console.error(`Failed to load image for ${outfit.name}:`, err);
        }
      }
      setOutfitImageUrls(urls);
    };
    loadOutfitUrls();
  }, [analysis?.generatedOutfits]);

  if (loading || analysis?.status === "IN_PROGRESS") {
    return (
      <Container fluid px={0} py={80}>
        <Stack align="center" gap="xl">
          <Loader size="lg" color="dark" />
          <Text ta="center" c="dimmed">{t("preparing")}</Text>
          <Progress value={analysis?.progress || 0} size="sm" w="100%" color="dark" />
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid px={0} py={80}>
        <Stack align="center" gap="lg">
          <Title order={2}>Something went wrong</Title>
          <Text c="dimmed">{error}</Text>
          <Button variant="outline" color="dark" onClick={() => router.push("/kibbe-body-type-test")}>
            Try Again
          </Button>
        </Stack>
      </Container>
    );
  }

  if (!result) {
    return (
      <Container fluid px={0} py={80}>
        <Stack align="center" gap="lg">
          <Title order={2}>No Results Found</Title>
          <Text c="dimmed">We couldn't find your analysis.</Text>
          <Button variant="outline" color="dark" onClick={() => router.push("/kibbe-body-type-test")}>
            Start Over
          </Button>
        </Stack>
      </Container>
    );
  }

  if (analysis.result.reportVersion === 3) return <>
    <KibbeGeneratedReport result={analysis.result as GeneratedKibbeReport} outfits={analysis.generatedOutfits || []} imageUrls={outfitImageUrls} analysisId={analysisId} sample={sample} />
    {colorAnalysisId && <ColorAnalysisResults colorAnalysis={colorAnalysis} loading={colorAnalysisLoading} analysisId={colorAnalysisId} />}
  </>;

  // Normalize body type to Title Case (e.g., "SOFT DRAMATIC" -> "Soft Dramatic")
  const normalizeBodyType = (type: string): string => {
    return type
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const normalizedType = normalizeBodyType(result.bodyType);

  const celebrities = kibbeTypeCelebrities[normalizedType] || [];
  const identity = typeIdentityStatements[normalizedType] || { headline: "", strength: "" };
  const frustrations = typeFrustrations[normalizedType] || [];
  const traits = traitComparisons[normalizedType] || {};

  return (
    <Container fluid px={0} py={48}>
      <Stack gap={48}>

        {/* ============ HERO ============ */}
        <Box>
          <Text fz="xs" fw={500} tt="uppercase" c="dimmed" mb={8} style={{ letterSpacing: 2 }}>
            {analysis?.identitySource === "user" ? "Your chosen Kibbe type" : sample ? "Illustrative AI Kibbe analysis" : "Your AI Kibbe body type"}
          </Text>
          <Title order={1} fz={{ base: 36, md: 48 }} fw={300} mb={16} style={{ letterSpacing: -1 }}>
            {normalizedType}
          </Title>
          {!result.wardrobePlan && <Text fz="lg" c="dimmed" maw={480} mb={8}>
            Explore {identity.strength}.
          </Text>}
          {!result.wardrobePlan && celebrities.length > 0 && (
            <Text fz="sm" c="dimmed">
              Community comparisons (not verified): {celebrities.slice(0, 3).join(", ")}.
            </Text>
          )}
          <Text fz="xs" c="dimmed" mt={16}>
            {analysis?.identitySource === "user" ? "Built around the identity you selected. This is not an independent confirmation of your type." : "A photo-based AI estimate. Use it as a starting point and compare the suggestions with outfits you own."}
          </Text>
        </Box>

        {/* ============ MOOD STRIP ============ */}
        {!result.wardrobePlan && <KibbeMoodStrip type={normalizedType} />}

        {/* ============ PROFILE ============ */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>{analysis?.identitySource === "user" ? "About your chosen type" : "Why this Kibbe type"}</Title>
          <Text fz="md" c="dimmed" mb={24} style={{ lineHeight: 1.8 }}>
            {result.explanation}
          </Text>

          {result.personalizedInsights && (
            <Box>
                <Text fz="sm" fw={600} mb={4}>What stands out in the photo</Text>
                <Text fz="sm" c="dimmed">{result.personalizedInsights.standoutFeature}</Text>
            </Box>
          )}
        </Box>

        {/* ============ TECHNICAL DETAILS ============ */}
        {Object.keys(result.boneStructure || {}).length > 0 && <Accordion variant="default" defaultValue="technical">
          <Accordion.Item value="technical">
            <Accordion.Control>
              <Text fz="sm" fw={500}>Your proportion analysis</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing={24} mt={16}>
                <Box>
                  <Text fz="xs" tt="uppercase" c="dimmed" mb={8}>Bone Structure</Text>
                  {Object.entries(result.boneStructure).map(([key, value]) => (
                    <Box key={key} mb={8}>
                      <Text fz="xs" c="dimmed">{key.replace(/([A-Z])/g, " $1").trim()}</Text>
                      <Text fz="sm">{value}</Text>
                    </Box>
                  ))}
                </Box>
                <Box>
                  <Text fz="xs" tt="uppercase" c="dimmed" mb={8}>Curve and proportions</Text>
                  {Object.entries(result.bodyFlesh).map(([key, value]) => (
                    <Box key={key} mb={8}>
                      <Text fz="xs" c="dimmed">{key.replace(/([A-Z])/g, " $1").trim()}</Text>
                      <Text fz="sm">{value}</Text>
                    </Box>
                  ))}
                </Box>
                <Box>
                  <Text fz="xs" tt="uppercase" c="dimmed" mb={8}>Facial Features</Text>
                  {Object.entries(result.facialFeatures).map(([key, value]) => (
                    <Box key={key} mb={8}>
                      <Text fz="xs" c="dimmed">{key}</Text>
                      <Text fz="sm">{value}</Text>
                    </Box>
                  ))}
                </Box>
              </SimpleGrid>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>}

        {/* ============ WHY NOT OTHER TYPES ============ */}
        {result.whyNotOtherTypes && result.whyNotOtherTypes.length > 0 && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>{analysis?.identitySource === "user" ? "Compare your chosen type" : "Why this type over the alternatives"}</Title>
            <Stack gap={16}>
              {result.whyNotOtherTypes.map((comp, i) => (
                <Box key={i}>
                  <Text fz="sm" fw={500} mb={4}>Compare with {comp.type}</Text>
                  <Text fz="sm" c="dimmed">{comp.reason}</Text>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {result.personalizedInsights && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>Styling for your result</Title>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
              <Box>
                <Text fz="sm" fw={600} mb={4}>Dress for your strength</Text>
                <Text fz="sm" c="dimmed">{result.personalizedInsights.dressForStrength}</Text>
              </Box>
              <Box>
                <Text fz="sm" fw={600} mb={4}>A styling experiment to try</Text>
                <Text fz="sm" c="dimmed">{result.personalizedInsights.minimizeAdvice}</Text>
              </Box>
            </SimpleGrid>
          </Box>
        )}

        {/* ============ BODY SNAPSHOT ============ */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>Common lines for this identity</Title>
          <Text size="sm" c="dimmed" mb="md">General type guidance, not measurements of your body.</Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={24}>
            <Box>
              <Text fz="xs" tt="uppercase" c="dimmed" mb={4}>Shoulders</Text>
              <Text fz="sm" fw={500}>{traits.shoulders || "—"}</Text>
            </Box>
            <Box>
              <Text fz="xs" tt="uppercase" c="dimmed" mb={4}>Vertical Line</Text>
              <Text fz="sm" fw={500}>{traits.vertical || "—"}</Text>
            </Box>
            <Box>
              <Text fz="xs" tt="uppercase" c="dimmed" mb={4}>Flesh/Curves</Text>
              <Text fz="sm" fw={500}>{traits.flesh || "—"}</Text>
            </Box>
            <Box>
              <Text fz="xs" tt="uppercase" c="dimmed" mb={4}>Overall</Text>
              <Text fz="sm" fw={500}>{traits.overall || "—"}</Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* ============ FRUSTRATIONS ============ */}
        {!result.wardrobePlan && frustrations.length > 0 && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>Fit experiments to try</Title>
            <Text fz="sm" c="dimmed" mb={16}>
              Understanding your type explains these frustrations:
            </Text>
            <Stack gap={8}>
              {frustrations.map((f, i) => (
                <Text key={i} fz="sm">• {f}</Text>
              ))}
            </Stack>
          </Box>
        )}

        {/* ============ SILHOUETTE ============ */}
        {!result.wardrobePlan && kibbeSilhouettes[normalizedType] && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>Your Ideal Silhouette</Title>
            <Box maw={300}>
              <img
                src={kibbeSilhouettes[normalizedType]}
                alt={`${normalizedType} silhouette`}
                style={{ width: "100%", height: "auto", display: "block" }}
                loading="lazy"
              />
            </Box>
          </Box>
        )}

        {/* ============ STYLE GUIDES ============ */}
        {!result.wardrobePlan && typeToGuideFamily[normalizedType] && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>Necklines, Fabrics & Patterns</Title>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={24}>
              <Box>
                <Text fz="sm" fw={500} mb={8}>Necklines</Text>
                <img
                  src={`/kibbe/guides/necklines/${typeToGuideFamily[normalizedType]}-necklines.png`}
                  alt="Necklines"
                  style={{ width: "100%", height: "auto", display: "block", borderRadius: 8 }}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </Box>
              <Box>
                <Text fz="sm" fw={500} mb={8}>Fabrics</Text>
                <img
                  src={`/kibbe/guides/fabrics/${typeToGuideFamily[normalizedType]}-fabrics.png`}
                  alt="Fabrics"
                  style={{ width: "100%", height: "auto", display: "block", borderRadius: 8 }}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </Box>
              <Box>
                <Text fz="sm" fw={500} mb={8}>Patterns</Text>
                <img
                  src={`/kibbe/guides/patterns/${typeToGuideFamily[normalizedType]}-patterns.png`}
                  alt="Patterns"
                  style={{ width: "100%", height: "auto", display: "block", borderRadius: 8 }}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </Box>
            </SimpleGrid>
          </Box>
        )}

        {/* ============ AI OUTFITS ============ */}
        {analysis?.generatedOutfits && analysis.generatedOutfits.length > 0 && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>Your outfit illustrations</Title>
            <Text size="sm" c="dimmed" mb="lg">AI illustrations can change details and proportions. Use these as styling ideas, not virtual fitting results.</Text>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={24}>
              {(analysis.generatedOutfits as GeneratedOutfit[]).map((outfit, i) => (
                <Box key={i}>
                  <Box mb={12} style={{ borderRadius: 8, overflow: "hidden", backgroundColor: "#f5f5f5", minHeight: 200 }}>
                    {outfitImageUrls[outfit.name] ? (
                      <img
                        src={outfitImageUrls[outfit.name]}
                        alt={outfit.name}
                        style={{ width: "100%", height: "auto", display: "block" }}
                        loading="lazy"
                      />
                    ) : (
                      <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
                        <Loader size="sm" color="dark" />
                      </Box>
                    )}
                  </Box>
                  <Text fz="xs" tt="uppercase" c="dimmed" mb={4}>{outfit.type}</Text>
                  <Text fz="sm" fw={500} mb={4}>{outfit.name}</Text>
                  <Text fz="xs" c="dimmed">{outfit.description}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        <KibbeWardrobePlan plan={result.wardrobePlan} result={result} analysisId={analysisId} sample={sample} />

        {/* ============ FIT RULES ============ */}
        {result.fitRules && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>How Clothes Should Fit You</Title>
            <Stack gap={16}>
              <Box>
                <Text fz="sm" fw={500} mb={4}>Shoulders</Text>
                <Text fz="sm" c="dimmed">{result.fitRules.shoulderFit}</Text>
              </Box>
              <Box>
                <Text fz="sm" fw={500} mb={4}>Hem length</Text>
                <Text fz="sm" c="dimmed">{result.fitRules.hemLength}</Text>
              </Box>
              <Box>
                <Text fz="sm" fw={500} mb={4}>Waist placement</Text>
                <Text fz="sm" c="dimmed">{result.fitRules.waistPlacement}</Text>
              </Box>
              <Box>
                <Text fz="sm" fw={500} mb={4}>Sleeves</Text>
                <Text fz="sm" c="dimmed">{result.fitRules.sleeveLength}</Text>
              </Box>
              <Box style={{ borderLeft: "2px solid #000", paddingLeft: 16, marginTop: 8 }}>
                <Text fz="sm" fw={600} mb={4}>The #1 rule for your type</Text>
                <Text fz="sm">{result.fitRules.proportionRule}</Text>
              </Box>
            </Stack>
          </Box>
        )}

        {/* ============ MISTAKES ============ */}
        {result.commonMistakes && result.commonMistakes.length > 0 && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>Common Mistakes</Title>
            <Accordion variant="default" styles={{ control: { padding: 0 }, content: { padding: 0, paddingTop: 8 } }}>
              {result.commonMistakes.map((mistake, i) => (
                <Accordion.Item key={i} value={`mistake-${i}`} style={{ borderBottom: "1px solid #eee" }}>
                  <Accordion.Control>
                    <Text fz="sm" fw={500}>{mistake.mistake}</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text fz="sm" c="dimmed" mb={8}>{mistake.why}</Text>
                    <Text fz="sm"><strong>Instead:</strong> {mistake.instead}</Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Box>
        )}

        {/* ============ SHOPPING ============ */}
        {result.shoppingTips && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>Shopping Notes</Title>
            <Stack gap={16}>
              <Box>
                <Text fz="sm">
                  <Text span fw={500}>Brands to try: </Text>
                  {result.shoppingTips.brandsToTry.join(", ")}
                </Text>
              </Box>
              <Box>
                <Text fz="sm">
                  <Text span fw={500}>Search for: </Text>
                  {result.shoppingTips.searchTerms.map(t => `"${t}"`).join(", ")}
                </Text>
              </Box>
              <Box>
                <Text fz="sm" fw={500} mb={4}>Budget tips</Text>
                {result.shoppingTips.budgetHacks.map((hack, i) => (
                  <Text key={i} fz="sm" c="dimmed">• {hack}</Text>
                ))}
              </Box>
              <Box>
                <Text fz="sm">
                  <Text span fw={500}>Avoid: </Text>
                  {result.shoppingTips.redFlags.map(f => `"${f}"`).join(", ")}
                </Text>
              </Box>
            </Stack>
          </Box>
        )}

        {/* ============ DO'S AND DON'TS ============ */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>Quick Reference</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={32}>
            <Box>
              <Text fz="sm" fw={500} mb={12}>Do</Text>
              <Stack gap={8}>
                {result.styleRecommendations.do.map((item, i) => (
                  <Text key={i} fz="sm" c="dimmed">• {item}</Text>
                ))}
              </Stack>
            </Box>
            <Box>
              <Text fz="sm" fw={500} mb={12}>Don't</Text>
              <Stack gap={8}>
                {result.styleRecommendations.dont.map((item, i) => (
                  <Text key={i} fz="sm" c="dimmed">• {item}</Text>
                ))}
              </Stack>
            </Box>
          </SimpleGrid>
        </Box>

        {/* ============ COLOR ANALYSIS ADD-ON ============ */}
        {analysis?.includeAnalysis && (
          <ColorAnalysisResults colorAnalysis={colorAnalysis} loading={colorAnalysisLoading} analysisId={colorAnalysisId || undefined} />
        )}


      </Stack>
    </Container>
  );
}
